"use client";

import { useMemo, useState } from "react";
import type { AgenticOverview, DashboardUsage } from "../lib/dashboardTypes";
import { formatDate, formatDay, formatIn, formatInt } from "../lib/format";
import { API_ACCENT, CARD } from "./agenticStyle";

function Meter({ used, limit, color }: { used: number; limit: number; color: string }) {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const warn = pct >= 90 ? "#e35e1c" : pct >= 75 ? "#f5c518" : color;
  return (
    <div
      className="h-2.5 w-full overflow-hidden rounded-full bg-[#0f1923]"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={limit}
      aria-valuenow={used}
    >
      <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: warn }} />
    </div>
  );
}

function PeriodCard({
  label,
  period,
  nowMs,
  costs,
  utcHint,
}: {
  label: string;
  period: DashboardUsage["month"];
  nowMs: number;
  costs: AgenticOverview["costs"];
  utcHint: string;
}) {
  const left = Math.max(0, period.limit - period.used);
  return (
    <div className="rounded-lg border border-[#2a475e]/70 bg-[#16202d] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-[#8f98a0]">{label}</p>
        <p className="text-xs text-[#8f98a0]" title={utcHint}>
          resets {formatIn(period.resetsAt, nowMs)}
        </p>
      </div>
      <p className="mt-2 text-2xl font-bold tabular-nums text-white">
        {formatInt(left)}
        <span className="ml-1.5 text-sm font-medium text-[#8f98a0]">units left</span>
      </p>
      <div className="mt-3">
        <Meter used={period.used} limit={period.limit} color={API_ACCENT} />
      </div>
      <p className="mt-2 text-xs tabular-nums text-[#8f98a0]">
        {formatInt(period.used)} of {formatInt(period.limit)} used
      </p>
      <p className="mt-3 text-xs leading-relaxed text-[#c7d5e0]">
        Enough for about <strong className="text-white">{formatInt(left / costs.lookup)}</strong> lookups,{" "}
        <strong className="text-white">{formatInt(Math.floor(left / costs.series))}</strong> history series or{" "}
        <strong className="text-white">{formatInt(Math.floor(left / costs.forecast))}</strong> forecasts.
      </p>
    </div>
  );
}

function DailyChart({ days }: { days: DashboardUsage["days"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...days.map((d) => d.units));
  const total = days.reduce((n, d) => n + d.units, 0);
  const shown = hover !== null ? days[hover] : null;
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-[#8f98a0]">Last 30 days</p>
        <p className="text-xs tabular-nums text-[#8f98a0]" aria-live="polite">
          {shown
            ? `${formatDay(shown.day)}: ${formatInt(shown.units)} units · ${formatInt(shown.requests)} calls${shown.errors ? ` · ${formatInt(shown.errors)} refunded` : ""}`
            : `${formatInt(total)} units in 30 days`}
        </p>
      </div>
      <div className="flex h-28 items-end gap-[3px]" onMouseLeave={() => setHover(null)}>
        {days.map((d, i) => (
          <button
            key={d.day}
            type="button"
            aria-label={`${formatDay(d.day)}: ${formatInt(d.units)} units`}
            onMouseEnter={() => setHover(i)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(null)}
            className="group relative flex h-full flex-1 items-end focus:outline-none"
          >
            <span
              className="block w-full rounded-t-sm transition-colors"
              style={{
                height: d.units > 0 ? `${Math.max(4, (d.units / max) * 100)}%` : "2px",
                backgroundColor: d.units > 0 ? (hover === i ? "#66c0f4" : API_ACCENT) : "#2a475e",
                opacity: hover === null || hover === i ? 1 : 0.55,
              }}
            />
          </button>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-[#5c6b7a]">
        <span>{formatDay(days[0]?.day ?? "")}</span>
        <span>today (UTC)</span>
      </div>
    </div>
  );
}

function EndpointTable({ usage }: { usage: DashboardUsage }) {
  if (usage.endpoints.length === 0) {
    return <p className="text-sm text-[#8f98a0]">No calls this month yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] text-left text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-[#8f98a0]">
            <th className="pb-2 font-medium">Endpoint</th>
            <th className="pb-2 font-medium">Via</th>
            <th className="pb-2 text-right font-medium">Calls</th>
            <th className="pb-2 text-right font-medium">Units</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#2a475e]/50">
          {usage.endpoints.map((e) => (
            <tr key={`${e.endpoint}:${e.channel}`}>
              <td className="py-2 pr-3">
                <span className="text-white">{e.title}</span>
                <span className="ml-2 font-mono text-[11px] text-[#5c6b7a]">{e.endpoint}</span>
              </td>
              <td className="py-2 pr-3 text-xs uppercase text-[#8f98a0]">{e.channel === "mcp" ? "MCP" : "REST"}</td>
              <td className="py-2 text-right tabular-nums text-[#c7d5e0]">{formatInt(e.requests)}</td>
              <td className="py-2 text-right tabular-nums text-white">{formatInt(e.units)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostTable({ overview }: { overview: AgenticOverview }) {
  const groups = useMemo(() => {
    const m = new Map<string, AgenticOverview["catalog"]>();
    for (const e of overview.catalog) m.set(e.group, [...(m.get(e.group) ?? []), e]);
    return [...m];
  }, [overview.catalog]);
  return (
    <details className="group rounded-lg border border-[#2a475e]/70 bg-[#16202d] p-4">
      <summary className="cursor-pointer list-none text-sm font-medium text-white marker:hidden">
        <span className="mr-2 inline-block transition-transform group-open:rotate-90">›</span>
        What each call costs
      </summary>
      <div className="mt-3 space-y-4">
        {groups.map(([group, items]) => (
          <div key={group}>
            <p className="mb-1.5 text-[11px] uppercase tracking-wider text-[#8f98a0]">{group}</p>
            <ul className="space-y-1">
              {items.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">
                    <span className="text-[#c7d5e0]">{e.title}</span>
                    <span className="ml-2 hidden font-mono text-[11px] text-[#5c6b7a] sm:inline">GET {e.path}</span>
                  </span>
                  <span className="flex-shrink-0 tabular-nums text-white">{e.units === 0 ? "free" : `${e.units} unit${e.units === 1 ? "" : "s"}`}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}

export function UsagePanel({ overview, nowMs }: { overview: AgenticOverview; nowMs: number }) {
  const usage = overview.usage;
  if (!usage) return null;
  const { costs, limits } = overview;
  return (
    <section className={CARD} aria-labelledby="usage-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="usage-heading" className="text-lg font-semibold text-white">
          Usage
        </h2>
        <p className="text-xs text-[#8f98a0]">
          Month of {formatDate(`${usage.monthStart}T00:00:00Z`, true).replace(/ \d+,/, "")} (UTC)
        </p>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <PeriodCard label="This month" period={usage.month} nowMs={nowMs} costs={costs} utcHint="On the 1st at 00:00 UTC" />
        <PeriodCard label="Today" period={usage.day} nowMs={nowMs} costs={costs} utcHint="At 00:00 UTC" />
      </div>
      <div className="mt-4 rounded-lg border border-[#2a475e]/70 bg-[#0f1923]/60 p-4 text-sm leading-relaxed text-[#c7d5e0]">
        <p>
          Every call costs <strong className="text-white">units</strong> by weight: a lookup (a game, a chart page, a
          search) is <strong className="text-white">{costs.lookup}</strong>, a history series is{" "}
          <strong className="text-white">{costs.series}</strong>, a launch forecast is{" "}
          <strong className="text-white">{costs.forecast}</strong>. Failed and invalid calls are free, and the usage
          call itself costs nothing.
        </p>
        <p className="mt-2">
          All your keys and the MCP server share one pool: {formatInt(limits.unitsPerMonth)} units a month, at most{" "}
          {formatInt(limits.unitsPerDay)} a day, and each key can make {limits.requestsPerMinutePerKey} requests a minute.
          When a pool runs out, calls answer <code className="rounded bg-[#16202d] px-1 text-[12px]">429 quota_exceeded</code> until
          it resets; nothing is ever charged extra.
        </p>
      </div>
      <div className="mt-5">
        <DailyChart days={usage.days} />
      </div>
      <div className="mt-6">
        <h3 className="mb-3 text-sm font-semibold text-white">This month by endpoint</h3>
        <EndpointTable usage={usage} />
      </div>
      <div className="mt-5">
        <CostTable overview={overview} />
      </div>
    </section>
  );
}
